import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useBreadcrumbs } from "../../../context/BreadcrumbsContext";
import { useLoading } from "../../../context/LoadingContext";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { useAuth } from "../../../context/AuthContext";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";

const Breadcrumbs = () => {
  const location = useLocation();
  const { breadcrumbs, setBreadcrumbs, getNameForId } = useBreadcrumbs();
  const { isLoading } = useLoading();
  const { user } = useAuth();

  useEffect(() => {
    const pathnames = location.pathname.split("/").filter(Boolean);

    const generateBreadcrumbs = () => {
      return pathnames.map((value, index) => {
        const path = `/${pathnames.slice(0, index + 1).join("/")}`;
        let name = value.replace(/-/g, ' ').charAt(0).toUpperCase() + value.replace(/-/g, ' ').slice(1);

        // Logique intelligente pour remplacer les IDs par des noms
        const isNumericId = /^\d+$/.test(value);

        if (isNumericId) {
          // Vérifier le contexte de l'URL pour déterminer le type d'ID
          const urlContext = pathnames.slice(0, index);

          // Pattern: /clients/{userId} - ID utilisateur
          if (urlContext.length === 1 && urlContext[0] === 'clients') {
            const mappedName = getNameForId(value);
            name = mappedName !== value ? mappedName : `Client ${value}`;
          }
          // Pattern: /clients/{userId}/capsule/{capsuleId} - ID capsule
          else if (urlContext.length === 3 && urlContext[0] === 'clients' && urlContext[2] === 'capsule') {
            const mappedName = getNameForId(value);
            name = mappedName !== value ? mappedName : `Capsule ${value}`;
          }
          // Pattern: /selections/{selectionId} - ID sélection
          else if (urlContext.length === 1 && urlContext[0] === 'selections') {
            const mappedName = getNameForId(value);
            name = mappedName !== value ? mappedName : `Sélection ${value}`;
          }
          // Pattern: /encode/{type}/{id} - ID d'encodage
          else if (urlContext.length === 2 && urlContext[0] === 'encode') {
            const type = urlContext[1];
            const mappedName = getNameForId(value);
            name = mappedName !== value ? mappedName : `${type.charAt(0).toUpperCase() + type.slice(1)} ${value}`;
          }
        }

        return { path, name };
      });
    };

    const newBreadcrumbs = generateBreadcrumbs();
    setBreadcrumbs(newBreadcrumbs);
  }, [location.pathname, setBreadcrumbs, getNameForId]);

  return (
    <div className="relative flex items-center justify-between">
      <nav className="h-10 w-full text-sm breadcrumbs border-solid border-t-0 border-l-0 border-r-0 border-b border-gray-300 mb-4 p-0">
        <ul className="pl-2 flex items-center space-x-2 h-full">
          <BreadcrumbItem to="/" name="Accueil" icon={HomeIcon} />
          {breadcrumbs.length > 0 &&
            breadcrumbs.map((breadcrumb, index) => (
              <BreadcrumbItem
                key={breadcrumb.path}
                to={breadcrumb.path}
                name={breadcrumb.name}
                isLast={index === breadcrumbs.length - 1}
                isLoading={isLoading}
              />
            ))}
        </ul>
      </nav>
      {user && user?.currentCredits && <div title="Crédits restant" className="absolute right-2 top-2 badge badge-primary badge-outline flex justify-center items-center gap-2">
        <ShoppingBagIcon className="w-4 h-4" />
        <span className="text-xs">
          {user?.currentCredits}
        </span>
      </div>}
    </div>
  );
};

const BreadcrumbItem = ({ to, name, isLast, isLoading, icon: Icon }) => {
  if (isLoading) {
    return (
      <li>
        <LoadingSpinner style="small" />
      </li>
    );
  }

  return (
    <li>
      {isLast ? (
        <span className="inline-flex gap-2 items-center">
          {Icon && <Icon />}
          {name}
        </span>
      ) : (
        <Link className="inline-flex gap-2 items-center text-primary hover:underline" to={to}>
          {Icon && <Icon />}
          {name}
        </Link>
      )}
    </li>
  );
};

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4 stroke-current">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    ></path>
  </svg>
);

export default Breadcrumbs;
